import { HeaderConfigurationResponse, PolicyHeaderFieldType, PolicyHeaderIssuingAuthorityType } from '../_types/manuals/draft';

type PolicyHeaderValue = {
  type: PolicyHeaderFieldType;
  value?: string | null;
  autoSetDate?: boolean;
  agencyLogo?: string | null;
  issuingAuthorityType?: PolicyHeaderIssuingAuthorityType;
  issuingAuthoritySignature?: string | null;
  issuingAuthorityName?: string | null;
};

/**
 * Returns true if any configured (enabled) policy headers have blank values.
 * Only applies to AGENCY-level manuals with headers enabled.
 * For ISSUING_AUTHORITY with SIGNATURE_AND_NAME type, blank is defined as
 * both the signature AND name being absent.
 */
export const checkForBlankHeaders = (
  headers: PolicyHeaderValue[] | undefined,
  headerConfiguration: HeaderConfigurationResponse | undefined,
  manualLevel: string
): boolean => {
  if (
    manualLevel !== 'AGENCY' ||
    headerConfiguration?.state !== 'ENABLED' ||
    !headerConfiguration?.order ||
    !headers ||
    headers.length === 0
  ) {
    return false;
  }

  const enabledTypes = new Set(
    headerConfiguration.order.filter((item) => item.state === 'ENABLED').map((item) => item.type)
  );

  const blankHeaders: string[] = [];

  headers.forEach((header) => {
    if (!enabledTypes.has(header.type)) return;
    let isBlank = false;
    switch (header.type) {
      case 'EFFECTIVE_DATE':
      case 'REVISED_DATE':
        if (header.autoSetDate) break;
        isBlank = !header.value || header.value.trim() === '';
        break;
      case 'AGENCY_LOGO':
        isBlank = !header.agencyLogo;
        break;
      case 'ISSUING_AUTHORITY': {
        const authType = header.issuingAuthorityType ?? 'SIGNATURE_AND_NAME';
        if (authType === 'SIGNATURE_AND_NAME') {
          isBlank =
            !header.issuingAuthoritySignature &&
            (!header.issuingAuthorityName || header.issuingAuthorityName.trim() === '');
        } else if (authType === 'SIGNATURE_ONLY') {
          isBlank = !header.issuingAuthoritySignature;
        } else {
          isBlank = !header.issuingAuthorityName || header.issuingAuthorityName.trim() === '';
        }
        break;
      }
      case 'CUSTOM1':
      case 'CUSTOM2':
        isBlank = !header.value || header.value.trim() === '';
        break;
    }
    if (isBlank) blankHeaders.push(header.type);
  });

  return blankHeaders.length > 0;
};
